import type { LatLng, LatLngBounds } from '../GeoMath';
import { frameBounds, frameToSite, type MapFrame } from '../MapFrame';
import type { PlanMass, PlanStructure, StructureArtifact } from '@/reference/SitePipeline';

/**
 * Buildings, walls and fences, measured rather than read off a photograph.
 *
 * ## Why this is the bigger half of the capture
 *
 * The terrain import proved the seam — a stage can be *supplied* instead of inferred. This is the
 * same move applied to the stage where inference costs most. Stage 5 is asked to trace a footprint
 * out of a roof plan, judge an eave height from a scale anchor, and split an L-shaped house into
 * masses by eye. Two of those three are matters of public record: OpenStreetMap carries the outline
 * as a surveyed ring and, for most mapped buildings, a `height` or `building:levels` tag with it.
 *
 * So the model's job changes from **invent the building** to **correct and complete this measured
 * one** — a far easier task, and one where a mistake lands on a named field instead of vanishing
 * into an invented object.
 *
 * ## What is measured here, and what is emphatically not
 *
 * | | Source | Status |
 * | - | ------ | ------ |
 * | Footprint ring | OSM way geometry | measured |
 * | Eave height | `height`, else `building:levels` × storey | measured where tagged |
 * | Walls and fences | `barrier=*` ways | measured where mapped |
 * | Roof shape and rise | — | **defaulted flat, and said so** |
 * | Wall and roof colour | — | **not attempted** |
 * | Which of these is *this site's* | — | **the designer's judgement** |
 *
 * The last row is the one that matters most. A bbox query returns the neighbour's garage and the
 * fence that is not yours with exactly the same confidence as the house you care about. Nothing in
 * the data distinguishes them, so nothing here pretends to: everything inside the frame is imported,
 * tagged with what it is, and left for the designer and the later stages to disown. That is why the
 * tagging step gets *more* load-bearing once this exists, not less.
 *
 * Roof form is defaulted to flat rather than guessed at. A guessed gable looks like knowledge; a flat
 * mass with `roof form not in the source data` in its notes looks like the open question it is, and
 * re-running stage 5 over it is then a *correction* with something to correct.
 *
 * ## Every decision is recorded
 *
 * {@link osmToStructures} returns a {@link StructureImportReport} beside the artifact: the exact
 * query, every element fetched, every one rejected with its reason and its tags, and per building the
 * height it got, which tag that came from, and whether its outline was clipped at the site edge.
 * A measured artifact that cannot be audited is just a different kind of black box.
 *
 * Pure: query strings in, parsed geometry out. The fetch belongs to the caller.
 */

export const OVERPASS_ENDPOINT = 'https://overpass-api.de/api/interpreter';

/** Public instances, in the order to try them. They rate-limit independently. */
export const OVERPASS_MIRRORS: readonly string[] = [
  OVERPASS_ENDPOINT,
  'https://overpass.kumi.systems/api/interpreter',
];

/** Typical floor-to-floor height, for turning `building:levels` into metres. */
export const DEFAULT_STOREY_METERS = 3;

/** What a barrier becomes when it carries no height of its own. */
const BARRIER_HEIGHTS: Record<string, number> = {
  fence: 1.8,
  wall: 2,
  retaining_wall: 1,
  hedge: 1.6,
  guard_rail: 0.8,
  handrail: 1.1,
};

/** Barrier values worth building. Anything else is a line on a map, not a thing in a garden. */
const BUILDABLE_BARRIERS = Object.keys(BARRIER_HEIGHTS);

/** `building` values that describe something other than a volume standing on the ground. */
const NON_BUILDINGS = new Set(['no', 'roof', 'bridge', 'construction']);

export class StructureSourceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StructureSourceError';
  }
}

/* ----------------------------------- Query ----------------------------------- */

export interface OverpassQueryOptions {
  /** Seconds the server may spend. Public instances refuse anything ambitious. */
  timeoutSeconds?: number;
  /** Include `barrier=*` ways — fences, walls, hedges. */
  barriers?: boolean;
}

/**
 * Overpass QL for everything built inside a bounding box.
 *
 * `out tags geom` rather than `out body` so each way arrives with its coordinates inline — the
 * alternative is a second pass resolving node ids, which triples the request count and buys nothing.
 * `building:part` is excluded at the query: those are sub-volumes of a 3D-mapped building and
 * importing them alongside their parent stacks two solids in the same place.
 */
export function overpassQuery(bounds: LatLngBounds, options: OverpassQueryOptions = {}): string {
  const timeout = Math.max(5, Math.trunc(options.timeoutSeconds ?? 30));
  const box = `${bounds.south.toFixed(7)},${bounds.west.toFixed(7)},${bounds.north.toFixed(7)},${bounds.east.toFixed(7)}`;
  const clauses = [`way["building"]["building"!~"^(no)$"](${box});`];
  if (options.barriers !== false) clauses.push(`way["barrier"](${box});`);

  return `[out:json][timeout:${timeout}];(${clauses.join('')});out tags geom;`;
}

/* ----------------------------------- Parse ----------------------------------- */

export interface OsmWay {
  id: string;
  tags: Record<string, string>;
  /** The way's own coordinates, as Overpass returns them with `out geom`. */
  geometry: LatLng[];
}

export function parseOverpass(payload: unknown): OsmWay[] {
  if (!isRecord(payload)) throw new StructureSourceError('Overpass returned no object.');

  const remark = payload['remark'];
  if (typeof remark === 'string' && /error|timed out|exceeded/i.test(remark)) {
    throw new StructureSourceError(`Overpass refused the query: ${remark}`);
  }

  const elements = payload['elements'];
  if (!Array.isArray(elements)) throw new StructureSourceError('Overpass returned no elements array.');

  const ways: OsmWay[] = [];
  for (const element of elements) {
    if (!isRecord(element) || element['type'] !== 'way') continue;
    const geometry = element['geometry'];
    if (!Array.isArray(geometry)) continue;

    const points: LatLng[] = [];
    for (const node of geometry) {
      if (!isRecord(node)) continue;
      const lat = Number(node['lat']);
      const lng = Number(node['lon']);
      if (Number.isFinite(lat) && Number.isFinite(lng)) points.push({ lat, lng });
    }
    if (points.length < 2) continue;

    ways.push({
      id: `way/${wayId(element['id'])}`,
      tags: readTags(element['tags']),
      geometry: points,
    });
  }
  return ways;
}

/* ---------------------------------- Import ---------------------------------- */

export interface StructureImportOptions {
  /** The frame everything is projected onto. Carries rotation and quad rectification. */
  frame: MapFrame;
  storeyMeters?: number;
  /** Height for a building the source says nothing about. */
  defaultBuildingMeters?: number;
  /** How thick a `barrier` way is built. Thin, but not so thin it z-fights. */
  wallThicknessMeters?: number;
  /** Footprints smaller than this are sheds-of-nothing — bin lockers, map noise. */
  minAreaSquareMeters?: number;
  /** Import fences and walls. Off gives buildings only. */
  barriers?: boolean;
  /** Hedges are planting, not masonry; off by default for that reason. */
  includeHedges?: boolean;
  /** The query that produced these ways, recorded verbatim so the report can be re-run by hand. */
  query?: string;
  /** The box that was asked for, for the same reason. */
  bounds?: LatLngBounds;
}

/** Everything that happened to one way that survived. */
export interface StructureTrace {
  osmId: string;
  name: string;
  kind: 'building' | 'barrier';
  /** The tag that decided it — `building=house`, `barrier=fence`. */
  classification: string;
  heightMeters: number;
  /** Which tag the height came from, or that nothing did. */
  heightSource: 'height' | 'building:levels' | 'barrier default' | 'default';
  /** The tag as written, so a bad parse is visible: `height=13`, `building:levels=2`. */
  heightEvidence: string;
  vertices: number;
  areaSquareMeters: number;
  /** True when the outline crossed the site edge and was cut to it. */
  clipped: boolean;
  masses: number;
}

export interface StructureRejection {
  osmId: string;
  reason: string;
  tags: Record<string, string>;
}

export interface StructureImportReport {
  sourceId: string;
  /** Exactly what was asked, so it can be pasted into overpass-turbo unchanged. */
  query: string;
  bounds: LatLngBounds;
  fetched: number;
  accepted: StructureTrace[];
  rejected: StructureRejection[];
  /** Fraction of accepted buildings whose height was measured rather than defaulted. */
  heightCoverage: number;
  warnings: string[];
}

/**
 * OSM ways to a `structures` artifact, on the frame's own axes.
 *
 * Every ring is projected with {@link frameToSite}, so a rotated or rectified frame is handled by the
 * same arithmetic as everything else and this function never learns what a latitude is. Outlines that
 * cross the site edge are clipped rather than dropped: the pipeline's own rule is that no structure
 * may overhang the terrain, and half a neighbour's house at the boundary is both true and useful,
 * where a floating wall is neither.
 */
export function osmToStructures(
  ways: readonly OsmWay[],
  options: StructureImportOptions,
): { artifact: StructureArtifact; report: StructureImportReport } {
  const frame = options.frame;
  const storey = options.storeyMeters ?? DEFAULT_STOREY_METERS;
  const defaultBuilding = options.defaultBuildingMeters ?? 6;
  const thickness = Math.max(0.05, options.wallThicknessMeters ?? 0.25);
  const minArea = options.minAreaSquareMeters ?? 4;

  const halfWidth = frame.widthMeters / 2;
  const halfLength = frame.lengthMeters / 2;

  const structures: PlanStructure[] = [];
  const accepted: StructureTrace[] = [];
  const rejected: StructureRejection[] = [];
  const warnings: string[] = [];

  for (const way of ways) {
    const tags = way.tags;
    const reject = (reason: string): void => {
      rejected.push({ osmId: way.id, reason, tags });
    };

    if (tags['building:part'] !== undefined && tags['building'] === undefined) {
      reject('a `building:part` — a sub-volume of a 3D-mapped building, which its parent already covers');
      continue;
    }
    if (tags['location'] === 'underground' || Number(tags['layer'] ?? '0') < 0) {
      reject('underground');
      continue;
    }

    const isBuilding = tags['building'] !== undefined && !NON_BUILDINGS.has(tags['building']);
    const barrier = tags['barrier'];
    const isBarrier =
      options.barriers !== false && barrier !== undefined && BUILDABLE_BARRIERS.includes(barrier);

    if (!isBuilding && !isBarrier) {
      reject(
        barrier !== undefined
          ? `barrier=${barrier} is not something this tool builds`
          : `building=${tags['building'] ?? '—'} does not describe a volume on the ground`,
      );
      continue;
    }
    if (isBarrier && barrier === 'hedge' && options.includeHedges !== true) {
      reject('barrier=hedge — planting rather than masonry, so stage 4 should own it');
      continue;
    }

    // Projection happens once, here. Everything after this is plane geometry in site metres.
    const projected = way.geometry.map((point) => {
      const { x, z } = frameToSite(frame, point);
      return [x, z] as [number, number];
    });
    if (projected.some(([x, z]) => !Number.isFinite(x) || !Number.isFinite(z))) {
      reject('the frame projection could not place this way');
      continue;
    }

    if (isBuilding) {
      const built = buildingFrom(way, projected, {
        halfWidth,
        halfLength,
        storey,
        defaultBuilding,
        minArea,
      });
      if ('reason' in built) {
        reject(built.reason);
        continue;
      }
      structures.push(built.structure);
      accepted.push(built.trace);
    } else {
      const built = barrierFrom(way, projected, {
        halfWidth,
        halfLength,
        thickness,
        barrier: barrier as string,
      });
      if ('reason' in built) {
        reject(built.reason);
        continue;
      }
      structures.push(built.structure);
      accepted.push(built.trace);
    }
  }

  const buildings = accepted.filter((trace) => trace.kind === 'building');
  const measured = buildings.filter((trace) => trace.heightSource !== 'default').length;
  const heightCoverage = buildings.length === 0 ? 0 : measured / buildings.length;

  if (buildings.length > 0 && heightCoverage < 1) {
    warnings.push(
      `${buildings.length - measured} of ${buildings.length} building(s) carried no height and were ` +
        `built at ${defaultBuilding} m. Re-run stage 5 to have those read off the photograph.`,
    );
  }
  if (buildings.length > 0) {
    warnings.push(
      'Roof form is not in OpenStreetMap for these buildings, so every mass is flat with no rise. ' +
        'Stage 5 re-run over this artifact is a correction with something to correct.',
    );
  }
  if (accepted.length === 0) {
    warnings.push(
      'Nothing built was mapped inside this frame. That is common outside towns — it means no data, ' +
        'not no buildings, and stage 5 should be run normally.',
    );
  }
  warnings.push(
    'A bounding box cannot tell whose building is whose: everything mapped inside the frame is here, ' +
      'including the neighbours. Tag what belongs to the site before running the later stages.',
  );

  return {
    artifact: {
      structures,
      notes:
        `${accepted.length} structure(s) imported from OpenStreetMap. Footprints and heights are ` +
        'measured; roof form and colour are not, and are left at their defaults for a later stage ' +
        'to correct.',
    },
    report: {
      sourceId: 'structures-osm',
      query: options.query ?? '(not recorded)',
      bounds: options.bounds ?? frameBounds(frame),
      fetched: ways.length,
      accepted,
      rejected,
      heightCoverage,
      warnings,
    },
  };
}

/* --------------------------------- Buildings --------------------------------- */

interface BuildingContext {
  halfWidth: number;
  halfLength: number;
  storey: number;
  defaultBuilding: number;
  minArea: number;
}

function buildingFrom(
  way: OsmWay,
  projected: [number, number][],
  context: BuildingContext,
): { structure: PlanStructure; trace: StructureTrace } | { reason: string } {
  const ring = openRing(projected);
  if (ring.length < 3) return { reason: 'fewer than three distinct corners' };

  const clipped = clipToRect(ring, context.halfWidth, context.halfLength);
  if (clipped.length < 3) return { reason: 'falls entirely outside the site' };

  const area = Math.abs(shoelace(clipped));
  if (area < context.minArea) {
    return { reason: `only ${area.toFixed(1)} m² inside the site — below the ${context.minArea} m² floor` };
  }

  const wasClipped = anyOutsideRect(ring, context.halfWidth, context.halfLength);
  const height = buildingHeight(way.tags, context.storey, context.defaultBuilding);
  const mass: PlanMass = {
    name: 'measured footprint',
    polygon: clipped,
    heightMeters: height.meters,
    // Flat and zero rise, deliberately: see the module note. A guessed gable reads as knowledge.
    roof: 'flat',
    roofRiseMeters: 0,
  };

  const name = buildingName(way.tags);
  const structure: PlanStructure = {
    id: `osm-${way.id.replace('/', '-')}`,
    kind: 'building',
    name,
    masses: [mass],
    clearanceMeters: 0.8,
    notes:
      `OpenStreetMap ${way.id}. Height ${height.meters.toFixed(1)} m from ${height.source === 'default' ? 'no source — defaulted' : height.evidence}. ` +
      'Roof form not in the source data; flat is a placeholder, not a reading.' +
      (wasClipped ? ' Outline clipped at the site edge.' : ''),
  };

  return {
    structure,
    trace: {
      osmId: way.id,
      name,
      kind: 'building',
      classification: `building=${way.tags['building'] ?? 'yes'}`,
      heightMeters: height.meters,
      heightSource: height.source,
      heightEvidence: height.evidence,
      vertices: clipped.length,
      areaSquareMeters: area,
      clipped: wasClipped,
      masses: 1,
    },
  };
}

interface HeightReading {
  meters: number;
  source: StructureTrace['heightSource'];
  evidence: string;
}

/**
 * A building's eave height, and where it came from.
 *
 * `height` in OSM is to the *top* of the structure, roof included, while the pipeline's
 * `heightMeters` is to the eaves — so a tagged height is shaded down slightly rather than used raw.
 * Ignoring that difference makes every mapped house about a storey too tall at the wall.
 */
function buildingHeight(
  tags: Record<string, string>,
  storey: number,
  fallback: number,
): HeightReading {
  const tagged = parseLength(tags['height'] ?? tags['building:height']);
  if (tagged !== null && tagged > 0) {
    return {
      meters: round2(Math.max(2, tagged * 0.85)),
      source: 'height',
      evidence: `height=${tags['height'] ?? tags['building:height']} (to ridge; eaves taken at 0.85×)`,
    };
  }

  const levels = Number(tags['building:levels']);
  if (Number.isFinite(levels) && levels > 0) {
    return {
      meters: round2(levels * storey),
      source: 'building:levels',
      evidence: `building:levels=${tags['building:levels']} × ${storey} m`,
    };
  }

  return { meters: fallback, source: 'default', evidence: 'no height or level tag' };
}

function buildingName(tags: Record<string, string>): string {
  if (tags['name']) return tags['name'];
  const number = tags['addr:housenumber'];
  const street = tags['addr:street'];
  if (number && street) return `${number} ${street}`;
  const kind = tags['building'] ?? 'building';
  return kind === 'yes' ? 'Building' : capitalize(kind.replace(/_/g, ' '));
}

/* --------------------------------- Barriers --------------------------------- */

interface BarrierContext {
  halfWidth: number;
  halfLength: number;
  thickness: number;
  barrier: string;
}

/**
 * A fence or wall as a run of thin rectangular masses, one per segment.
 *
 * Per segment rather than one buffered polygon, and that is deliberate. Offsetting a polyline
 * correctly means mitring every joint and handling the self-intersections a tight corner produces —
 * a genuine computational-geometry problem for a shape nobody will look at closely. A rectangle per
 * segment is exact at the segment, wrong by a sliver at each joint, and every mass stays rectangular,
 * which is what the compiler needs to give it an honest flat top.
 */
function barrierFrom(
  way: OsmWay,
  projected: [number, number][],
  context: BarrierContext,
): { structure: PlanStructure; trace: StructureTrace } | { reason: string } {
  const line = openRing(projected, false);
  if (line.length < 2) return { reason: 'fewer than two distinct points' };

  const height = parseLength(way.tags['height']);
  const meters = height !== null && height > 0 ? round2(height) : (BARRIER_HEIGHTS[context.barrier] ?? 1.8);
  const half = context.thickness / 2;

  const masses: PlanMass[] = [];
  let clipped = false;

  for (let index = 0; index < line.length - 1; index += 1) {
    const [x0, z0] = line[index] as [number, number];
    const [x1, z1] = line[index + 1] as [number, number];
    const dx = x1 - x0;
    const dz = z1 - z0;
    const length = Math.hypot(dx, dz);
    if (length < 0.05) continue;

    // Normal to the segment, scaled to half the wall's thickness.
    const nx = (-dz / length) * half;
    const nz = (dx / length) * half;

    const quad: [number, number][] = [
      [x0 + nx, z0 + nz],
      [x1 + nx, z1 + nz],
      [x1 - nx, z1 - nz],
      [x0 - nx, z0 - nz],
    ];
    if (anyOutsideRect(quad, context.halfWidth, context.halfLength)) clipped = true;
    const cut = clipToRect(quad, context.halfWidth, context.halfLength);
    if (cut.length < 3) continue;

    masses.push({
      name: `run ${index + 1}`,
      polygon: cut,
      heightMeters: meters,
      roof: 'flat',
      roofRiseMeters: 0,
    });
  }

  if (masses.length === 0) return { reason: 'falls entirely outside the site' };

  const name = way.tags['name'] ?? capitalize(context.barrier.replace(/_/g, ' '));
  const area = masses.reduce((sum, mass) => sum + Math.abs(shoelace(mass.polygon)), 0);

  return {
    structure: {
      id: `osm-${way.id.replace('/', '-')}`,
      kind: 'building',
      name,
      masses,
      clearanceMeters: 0,
      notes:
        `OpenStreetMap ${way.id}, barrier=${context.barrier}, built as a ${context.thickness} m ` +
        `wall ${meters} m high in ${masses.length} run(s). This tool has no fence primitive, so a ` +
        'barrier is a thin flat-topped mass — right for a wall, and a solid stand-in for a fence.',
    },
    trace: {
      osmId: way.id,
      name,
      kind: 'barrier',
      classification: `barrier=${context.barrier}`,
      heightMeters: meters,
      heightSource: height !== null && height > 0 ? 'height' : 'barrier default',
      heightEvidence: height !== null && height > 0 ? `height=${way.tags['height']}` : `${context.barrier} default`,
      vertices: line.length,
      areaSquareMeters: area,
      clipped,
      masses: masses.length,
    },
  };
}

/* ---------------------------------- Geometry ---------------------------------- */

/**
 * Drop the repeated closing vertex and any duplicate points.
 *
 * OSM closes a ring by repeating its first node, and the pipeline's schema says explicitly that the
 * last vertex must not repeat the first — so this is a format conversion, not a cleanup.
 */
export function openRing(points: readonly [number, number][], closed = true): [number, number][] {
  const out: [number, number][] = [];
  for (const point of points) {
    const last = out[out.length - 1];
    if (last && Math.hypot(last[0] - point[0], last[1] - point[1]) < 1e-6) continue;
    out.push([point[0], point[1]]);
  }
  if (closed && out.length > 1) {
    const first = out[0] as [number, number];
    const last = out[out.length - 1] as [number, number];
    if (Math.hypot(first[0] - last[0], first[1] - last[1]) < 1e-6) out.pop();
  }
  return out;
}

/**
 * Sutherland–Hodgman against the site rectangle.
 *
 * Clipping rather than dropping, because the pipeline forbids a structure that overhangs the terrain
 * edge — a footprint half over the boundary would otherwise leave a wall hanging in space. Convex
 * clip region, arbitrary subject polygon, which is exactly the case this algorithm is exact for.
 */
export function clipToRect(
  polygon: readonly [number, number][],
  halfWidth: number,
  halfLength: number,
): [number, number][] {
  const edges: { inside: (p: [number, number]) => boolean; cut: (a: [number, number], b: [number, number]) => [number, number] }[] = [
    {
      inside: (p) => p[0] >= -halfWidth,
      cut: (a, b) => interpolate(a, b, (-halfWidth - a[0]) / (b[0] - a[0])),
    },
    {
      inside: (p) => p[0] <= halfWidth,
      cut: (a, b) => interpolate(a, b, (halfWidth - a[0]) / (b[0] - a[0])),
    },
    {
      inside: (p) => p[1] >= -halfLength,
      cut: (a, b) => interpolate(a, b, (-halfLength - a[1]) / (b[1] - a[1])),
    },
    {
      inside: (p) => p[1] <= halfLength,
      cut: (a, b) => interpolate(a, b, (halfLength - a[1]) / (b[1] - a[1])),
    },
  ];

  let output = [...polygon];
  for (const edge of edges) {
    const input = output;
    output = [];
    for (let index = 0; index < input.length; index += 1) {
      const current = input[index] as [number, number];
      const previous = input[(index + input.length - 1) % input.length] as [number, number];
      const currentIn = edge.inside(current);
      const previousIn = edge.inside(previous);

      if (currentIn) {
        if (!previousIn) output.push(edge.cut(previous, current));
        output.push(current);
      } else if (previousIn) {
        output.push(edge.cut(previous, current));
      }
    }
    if (output.length === 0) return [];
  }
  return openRing(output);
}

/**
 * Whether any vertex lay outside the site, which is what "was clipped" actually means.
 *
 * Comparing vertex counts before and after does not answer it: a rectangle cut by a single edge
 * comes back as a smaller rectangle with the same four corners, so the count is unchanged and the
 * building has still lost a third of itself. Asking about the input is both simpler and correct.
 */
export function anyOutsideRect(
  polygon: readonly [number, number][],
  halfWidth: number,
  halfLength: number,
): boolean {
  return polygon.some(
    ([x, z]) => x < -halfWidth || x > halfWidth || z < -halfLength || z > halfLength,
  );
}

function interpolate(a: [number, number], b: [number, number], t: number): [number, number] {
  const clamped = Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : 0;
  return [a[0] + (b[0] - a[0]) * clamped, a[1] + (b[1] - a[1]) * clamped];
}

/** Twice the signed area — sign is winding, magnitude halved is area. */
export function shoelace(polygon: readonly [number, number][]): number {
  let sum = 0;
  for (let index = 0; index < polygon.length; index += 1) {
    const [x0, z0] = polygon[index] as [number, number];
    const [x1, z1] = polygon[(index + 1) % polygon.length] as [number, number];
    sum += x0 * z1 - x1 * z0;
  }
  return sum / 2;
}

/* ----------------------------------- Report ----------------------------------- */

/**
 * The report as text, for the inspector's Reply pane.
 *
 * It goes where a model's raw answer would go, and that is the point: the question "why is there a
 * shed there" has the same answer shape whether a model or a survey put it there, so it belongs in
 * the same pane rather than behind a second UI nobody opens.
 */
export function describeStructureReport(report: StructureImportReport): string {
  const lines: string[] = [
    `Supplied by OpenStreetMap — this stage was not sent to a model.`,
    ``,
    `Query`,
    `  ${report.query}`,
    ``,
    `Fetched ${report.fetched} way(s) · accepted ${report.accepted.length} · rejected ${report.rejected.length}`,
    `Height measured on ${Math.round(report.heightCoverage * 100)}% of buildings`,
    ``,
  ];

  if (report.accepted.length > 0) {
    lines.push('ACCEPTED');
    lines.push(
      pad('osm id', 16) + pad('name', 26) + pad('what', 20) + pad('height', 9) + pad('from', 16) + pad('area m²', 9) + 'notes',
    );
    for (const trace of report.accepted) {
      lines.push(
        pad(trace.osmId, 16) +
          pad(trace.name, 26) +
          pad(trace.classification, 20) +
          pad(`${trace.heightMeters.toFixed(1)} m`, 9) +
          pad(trace.heightSource, 16) +
          pad(trace.areaSquareMeters.toFixed(0), 9) +
          [trace.clipped ? 'clipped at site edge' : '', trace.masses > 1 ? `${trace.masses} runs` : '']
            .filter(Boolean)
            .join(', '),
      );
      if (trace.heightSource !== 'default') lines.push(`${' '.repeat(16)}└ ${trace.heightEvidence}`);
    }
    lines.push('');
  }

  if (report.rejected.length > 0) {
    lines.push('REJECTED');
    for (const rejection of report.rejected) {
      lines.push(`  ${pad(rejection.osmId, 16)}${rejection.reason}`);
      lines.push(`  ${' '.repeat(16)}└ ${describeTags(rejection.tags)}`);
    }
    lines.push('');
  }

  if (report.warnings.length > 0) {
    lines.push('NOTES');
    for (const warning of report.warnings) lines.push(`  ! ${warning}`);
  }

  return lines.join('\n');
}

function describeTags(tags: Record<string, string>): string {
  const entries = Object.entries(tags).slice(0, 8);
  if (entries.length === 0) return 'no tags';
  return entries.map(([key, value]) => `${key}=${value}`).join(' ');
}

function pad(text: string, width: number): string {
  return text.length >= width ? `${text.slice(0, width - 1)} ` : text + ' '.repeat(width - text.length);
}

/* ---------------------------------- Helpers ---------------------------------- */

/**
 * A length tag as metres.
 *
 * OSM permits bare metres, an explicit unit, and imperial feet-and-inches (`12'6"`). All three turn
 * up on buildings, and reading `12'6"` as twelve metres would put a bungalow above the treeline.
 */
export function parseLength(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const text = raw.trim();

  const imperial = /^(\d+(?:\.\d+)?)\s*'\s*(?:(\d+(?:\.\d+)?)\s*")?$/.exec(text);
  if (imperial) {
    const feet = Number(imperial[1]);
    const inches = Number(imperial[2] ?? 0);
    return (feet * 12 + inches) * 0.0254;
  }

  const metric = /^(-?\d+(?:\.\d+)?)\s*(m|metres?|meters?)?$/i.exec(text);
  if (metric) return Number(metric[1]);

  const feetUnit = /^(-?\d+(?:\.\d+)?)\s*(ft|feet)$/i.exec(text);
  if (feetUnit) return Number(feetUnit[1]) * 0.3048;

  return null;
}

function readTags(value: unknown): Record<string, string> {
  if (!isRecord(value)) return {};
  const tags: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string') tags[key] = entry;
    else if (typeof entry === 'number') tags[key] = String(entry);
  }
  return tags;
}

/** An OSM element id, which arrives as a number but is only ever used as part of a string. */
function wayId(value: unknown): string {
  return typeof value === 'number' || typeof value === 'string' ? String(value) : '?';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function capitalize(text: string): string {
  return text.length === 0 ? text : text[0]?.toUpperCase() + text.slice(1);
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
